import { useState, useRef, useEffect, useCallback } from "react"
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../utils/api";

import { logout } from "../../utils/auth";
import moment from "moment";
import PaginationComponent from "../../Component/Pagination/pagination";
import AssignPlanModal from "./modals/AssignPlanModal";
import AssignAffiliateModal from "./modals/AssignAffiliateModal";

const Users = () => {
  const [userList, setUserList] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [userCount, setUserCount] = useState<number>(0);
  const [offBoard, setOffboard] = useState<number>(0);
  const [totalOnboard, setTotalOnboard] = useState<number>(0);
  const [planOnboardExpCount, setPlanOnboardExpCount] = useState<number>(0);
  const [totalExpried, setTotalExpried] = useState<number>(0);
  const [planActiveCount, setplanActiveCount] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<any>(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAssignAffiliateModal, setShowAssignAffiliateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const getUserList = useCallback(async (currentPage: number) => {
    try {
      setIsLoader(true)
      const response = await getRequest(`/user/userList-old?page=${currentPage}&activeTab=All`);
      if (response.status === 'success') {
        setUserList(response.data as any[])
        setTotalPages(response.totalPages);
        setTotalUsers(response.totalUsers);
        setTotalOnboard(response.onboard);
        setOffboard(response.offboard);
        setUserCount(response.userCount);
        setPlanOnboardExpCount(response.planOnboardExpCount);
        setplanActiveCount(response.planActiveCount);
        setTotalExpried(response.planExpiredCount);
        setIsLoader(false);
      }
      else if (response.status == 'session_expired') {
        setIsLoader(false)
        logout()
      }
      else {
        setIsLoader(false)
        toast.error(response.message);
      }
    } catch (error) {
      setIsLoader(false)
      toast.error("Something went wrong!");
    }
  }, []);

  const isFirstRender = useRef(false); // Track first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = true; // Skip first render
      return;
    }
    getUserList(page);
  }, [page]);

  type SubscriptionStatus = "ACTIVE" | "RENEW_SOON" | "EXPIRED";

  const checkSubscriptionStatus = (subscriptionDate: string): SubscriptionStatus => {
    const today = moment().startOf("day");
    const expiry = moment(subscriptionDate).startOf("day");
    // Expired
    if (expiry.isBefore(today)) {
      return "EXPIRED";
    }
    const diffDays = expiry.diff(today, "days");
    // Under or equal to 7 days
    if (diffDays <= 7) {
      return "RENEW_SOON";
    }
    // More than 7 days
    return "ACTIVE";
  };

  const handleAssignPlan = async (userId: string, data: any) => {
    try {
      // Example API call
      // const response = await postRequest(`/user/assign-plan`, { userId, ...data });
      toast.success("Plan assigned successfully!");
      getUserList(page); // Refresh the list
    } catch (error) {
      toast.error("Failed to assign plan");
    }
  };

  const handleDirectLogin = async (userId: string) => {
    try {
      const response = await postRequest(`/user/admin-login-as-user`, { user_id: userId });

      if (response.status === 'success') {
        toast.success("Opening user dashboard...");

        // 👇 TEMPORARILY save the user data payload so the new tab can read it
        localStorage.setItem("temp_impersonate_data", JSON.stringify(response.data));

        // Open the new AutoLogin page in a NEW tab, passing the token
        window.open(`https://app.uomox.com/auto-login?token=${response.token}`, '_blank');

      } else {
        toast.error(response.message || "Failed to login as this user");
      }
    } catch (error) {
      toast.error("Something went wrong during direct login.");
    }
  };

  return (
    <div className="hk-pg-wrapper">
      <div className="hk-pg-body py-0">
        <div className="page-wrap">
          <div className="page-content ps-0">
            <div className="page-detail-wrap">
              <header className="page-header">
                <div>
                  <div className="page-title">
                    <h2 className="mb-0">Manage Users</h2>
                  </div>
                  <p>Control and organize all user accounts in one place</p>
                </div>
                <div className="page-options-wrap">

                  <div className="v-separator d-lg-inline-block d-none"></div>
                  {/* <button type="button" className="btn btn-dark btn-rounded">
                    <span>
                      <span className="icon"><i className="ri-add-line bticon"></i></span>
                      <span>Create New</span>
                    </span>
                  </button> */}

                  <a className="btn btn-icon btn-flush-dark btn-rounded flush-soft-hover hk-navbar-togglable d-lg-inline-block d-none"
                    href="#" data-bs-toggle="tooltip" data-bs-placement="top" title=""
                    data-bs-original-title="Collapse">
                    <span className="btn-icon-wrap">
                      <span className="feather-icon"><i className="ri-arrow-up-s-line ri-lg"></i></span>
                      <span className="feather-icon d-none"><i className="ri-arrow-down-s-line ri-lg"></i></span>
                    </span>
                  </a>
                </div>
              </header>
              <div className="page-body">
                <div data-simplebar className="nicescroll-bar">
                  <div className="container-fluid">
                    <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3">
                      <div className="col">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <h2 className="mb-1">{totalUsers}</h2>
                                <p className="fw-medium text-muted mb-0">Total Users</p>
                              </div>
                              <div>
                                <div className="avatar avatar-icon avatar-soft-primary avatar-sm">
                                  <span className="initial-wrap">
                                    <i className="ri-group-line"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <h2 className="mb-1">{totalOnboard ?? 0}</h2>
                                <p className="fw-medium text-muted mb-0">Total Onboard Users</p>
                              </div>
                              <div>
                                <div className="avatar avatar-icon avatar-soft-success avatar-sm">
                                  <span className="initial-wrap">
                                    <i className="ri-group-line"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <h2 className="mb-1">{offBoard}</h2>
                                <p className="fw-medium text-muted mb-0">Total Off board Users</p>
                              </div>
                              <div>
                                <div className="avatar avatar-icon avatar-soft-warning avatar-sm">
                                  <span className="initial-wrap">
                                    <i className="ri-group-line"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <h2 className="mb-1">{planActiveCount}</h2>
                                <p className="fw-medium text-muted mb-0">Acive Plan</p>
                              </div>
                              <div>
                                <div className="avatar avatar-icon avatar-soft-danger avatar-sm">
                                  <span className="initial-wrap">
                                    <i className="ri-calendar-line"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <div>
                                <h2 className="mb-1">{totalExpried}</h2>
                                <p className="fw-medium text-muted mb-0">Plan Expired</p>
                              </div>
                              <div>
                                <div className="avatar avatar-icon avatar-soft-danger avatar-sm">
                                  <span className="initial-wrap">
                                    <i className="ri-calendar-line"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="card">
                          <div className="card-header card-header-action">
                            <h6>All User List</h6>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-hover mb-0">
                              <thead>
                                <tr>
                                  <th className="text-center" style={{ width: '30px' }}>
                                    <input type="checkbox" className="form-check-input check-select-all" id="customCheck1" />
                                  </th>

                                  <th>ID</th>
                                  <th>Company Details</th>
                                  <th>User Details</th>
                                  <th className="text-center">OS</th>
                                  <th>Plan & Cycle</th>
                                  <th>Next Billing</th>
                                  <th className="text-center">Status</th>
                                  <th>Create Date</th>
                                  <th>Last Login</th>
                                  <th className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userList && userList.length > 0 ? (
                                  userList.map((item, index) => (
                                    <tr>
                                      <td className="text-center">
                                        <input type="checkbox" className="form-check-input check-select-all" id="customCheck1" />
                                      </td>
                                      <td><a href="#" className="fw-bold">#{item.user_unique_id}</a></td>
                                      <td>
                                        <div className="media align-items-center">
                                          <div className="media-head me-2">
                                            <div className="avatar avatar-xss rounded">
                                              <img src="dist/img/logo-avatar-3.png" alt="user" className="avatar-img" />
                                            </div>
                                          </div>
                                          <div className="media-body">
                                            <h6 className="fs-13 mb-0 eclps-name-175">{item.user_companyName ?? "–"}</h6>
                                            <p className="mb-0 fs-12">{item.user_email}</p>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="eclps-name-100">{item.user_first_name} {item.user_last_name}</span>
                                      </td>

                                      <td className="text-center">
                                        {item.user_whatsapp && item.user_whatsapp.isWabaConnected ?
                                          <i className="ri-check-fill text-success ri-lg"></i>
                                          : <i className="ri-close-fill text-danger ri-lg"></i>
                                        }
                                      </td>
                                      <td>
                                        <a href="#">{item.user_plan_name}</a>
                                        <p className="mb-0 text-capitalize">{item.user_plan_type}</p>
                                      </td>
                                      <td><span className={`badge ${(checkSubscriptionStatus(item.user_plan_end_date) === "EXPIRED" && (item.user_whatsapp && item.user_whatsapp.isWabaConnected)) ? " bg-danger"
                                        : (item.user_whatsapp && !item.user_whatsapp.isWabaConnected) ? " bg-warning"
                                          : "bg-secondary"
                                        }`}>
                                        {moment(item.user_plan_end_date).format('DD MMM, YYYY')}</span>
                                      </td>
                                      <td className="text-center">
                                        {item.user_status === 1 && <span className="badge bg-success">ACTIVE</span>}
                                        {item.user_status === 0 && <span className="badge bg-warning">INACTIVE</span>}
                                        {item.user_status === 2 && <span className="badge bg-danger">TRASH</span>}
                                      </td>
                                      <td>{moment(item.createdAt).format('DD MMM, YYYY')}</td>
                                      <td>{moment(item.updatedAt).format('DD MMM, YYYY')}</td>
                                      <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                          <div className="d-flex gap-1">
                                            <a className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover" data-bs-toggle="tooltip" data-placement="top" title="" data-bs-original-title="View" href={`/customers-and-users/view-user/${item._id}`} >
                                              <span className="icon">
                                                <i className="ri-eye-line"></i>
                                              </span>
                                            </a>
                                            <div className="dropdown dropdown-static">
                                              <button className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover dropdown-toggle no-caret" aria-expanded="false" data-bs-toggle="dropdown">
                                                <span className="icon">
                                                  <i className="ri-more-2-fill"></i>
                                                </span>
                                              </button>
                                              <div className="dropdown-menu dropdown-menu-end" data-popper-placement="bottom-end">
                                                <a
                                                  className="dropdown-item"
                                                  href="#"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleDirectLogin(item._id);
                                                  }}
                                                >
                                                  <span className="dropdown-icon"><i className="ri-login-circle-line text-primary"></i></span>
                                                  <span>Login as User</span>
                                                </a>
                                                <a
                                                  className="dropdown-item"
                                                  href="#"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedUser(item);
                                                    setShowPlanModal(true);
                                                  }}
                                                >
                                                  <span className="dropdown-icon"><i className="ri-vip-crown-line"></i></span>
                                                  <span>Assign Plan</span>
                                                </a>
                                                <a
                                                  className="dropdown-item"
                                                  href="#"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedUser(item);
                                                    setShowAssignAffiliateModal(true);
                                                  }}
                                                >
                                                  <span className="dropdown-icon"><i className="ri-shield-user-line"></i></span>
                                                  <span>Assign Affiliate</span>
                                                </a>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={10}>
                                      <div className="text-center">No Record Found</div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="card-footer d-flex justify-content-between align-items-center">

                            <div className="col-sm">
                              <div className="text-muted">Showing <span className="fw-semibold">{userList?.length || 0}</span> of <span className="fw-semibold">{totalUsers || 0}</span> Results</div>
                            </div>
                            {totalPages > 0 && <PaginationComponent totalPages={totalPages} currentPage={page} setCurrentPage={setPage} />}

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AssignPlanModal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        user={selectedUser}
        onSuccess={() => {
          getUserList(page);
        }}
      />
      <AssignAffiliateModal
        show={showAssignAffiliateModal}
        onHide={() => { getUserList(page); setShowAssignAffiliateModal(false) }}
        userData={selectedUser}
      />
    </div>
  )
}
export default Users